import { useMemo } from 'react';
import { Checkbox, Input, Spin, Typography } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  ItemsWrapper,
  normalizeText,
} from '../../../components/ModelInstanceFilter';
import { useFilterGroupContext } from './FilterGroupContext';
import useFilterOptions from './useFilterOptions';
import type { IFilterProps } from './Filter';

const { Text } = Typography;

interface IProps {
  childKey: string;
  child: IFilterProps;
  active: boolean;
}

/**
 * One child filter's search + checkbox picker. Mounted once per child and kept
 * mounted for the lifetime of the group popup (visibility toggled via `active`)
 * so each child keeps its own search text and loaded/paginated data independently
 * instead of resetting every time the active child changes.
 */
const FilterGroupChildPane: React.FC<IProps> = ({
  childKey,
  child,
  active,
}) => {
  const { isVisible, context, draftSelections, setChildSelection } =
    useFilterGroupContext();
  const {
    filters,
    isLoading,
    onPopupScroll,
    isLocalSearch,
    search,
    setSearch,
  } = useFilterOptions({
    options: child.options,
    modelInstanceOptions: child.modelInstanceOptions,
    modelInstancesOptions: child.modelInstancesOptions,
    isVisible,
    context,
  });

  const searchedFilters = useMemo(() => {
    if (!isLocalSearch) {
      return filters;
    }
    const term = normalizeText(search);
    return filters.filter((f: any) =>
      normalizeText(f.label || '').includes(term)
    );
  }, [filters, isLocalSearch, search]);

  const localSelected = draftSelections[childKey] || [];

  const handleChangeFilter = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    const next = localSelected.filter(item => item !== value);
    if (checked) {
      next.push(value);
    }
    setChildSelection(childKey, next);
  };

  return (
    <ContentPane $active={active}>
      <Input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search"
        prefix={<SearchOutlined />}
        allowClear
        size="small"
        style={{ marginBottom: 8 }}
      />
      <ItemsWrapper onScroll={onPopupScroll}>
        {searchedFilters.map((f: any) => (
          <div key={`${f.label}_${f.value}`}>
            <Checkbox
              name={f.label}
              value={f.value}
              checked={localSelected.includes(f.value)}
              onChange={handleChangeFilter}
            >
              {f.label}
            </Checkbox>
          </div>
        ))}
        {isLoading && <Spin size="small" />}
        {!isLoading && searchedFilters.length === 0 && (
          <Text type="secondary">No data</Text>
        )}
      </ItemsWrapper>
    </ContentPane>
  );
};

export default FilterGroupChildPane;

const ContentPane = styled.div<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  flex: 1;
  min-width: 200px;
`;
